import IconAccountBox from '@iconify/icons-material-symbols/account-box-outline';
import IconCable from '@iconify/icons-material-symbols/cable';
import IconCellWifi from '@iconify/icons-material-symbols/cell-wifi';
import IconContactPage from '@iconify/icons-material-symbols/contact-page-outline';
import IconHotelClass from '@iconify/icons-material-symbols/hotel-class-outline';
import IconLightbulb from '@iconify/icons-material-symbols/lightbulb-outline';
import IconLocalLibrary from '@iconify/icons-material-symbols/local-library-outline';
import IconMessage from '@iconify/icons-material-symbols/mail-outline';
import IconSettings from '@iconify/icons-material-symbols/manage-accounts';
import IconRequestPage from '@iconify/icons-material-symbols/request-page';
import IconSchool from '@iconify/icons-material-symbols/school-outline';
import { useTranslation } from 'react-i18next';
import type { AccountAuthorisationExtendedDto } from '#pie/auth/AccountAuthorisationExtendedDto';
import { TicketCounter } from '#pie/components/ticket-counter/TicketCounter';
import { AuthorizationRoles } from '#pie/internal/api/pie-bff';
import { hasRole } from '#pie/internal/libs/auth';
import type { Menu } from './SidebarMenu';

export const isMenuItemAllowed = (
  menuItem: Menu,
  currentAccount?: AccountAuthorisationExtendedDto,
): boolean => {
  const isMenuUnrestricted = !menuItem.allowedRoles;
  const userHasRole =
    currentAccount?.roles &&
    menuItem.allowedRoles &&
    hasRole(menuItem.allowedRoles, currentAccount.roles);

  const marketRoleCheck =
    !menuItem.allowedMarketRoles ||
    (currentAccount?.marketRole &&
      menuItem.allowedMarketRoles.some((role) =>
        currentAccount.marketRole.includes(role),
      ));

  return isMenuUnrestricted || (!!userHasRole && !!marketRoleCheck);
};

interface MenuProps {
  knowledgeFilterParams?: string;
  ticketFilterParams?: string;
}

export const menu = ({
  knowledgeFilterParams,
  ticketFilterParams,
}: MenuProps): { main: Menu[]; bottom: Menu[] } => {
  const { t } = useTranslation();
  const mainMenuItems: Menu[] = [
    {
      adornment: (isOpen) => <TicketCounter isOpen={isOpen} />,
      allowedRoles: [AuthorizationRoles.Tickets],
      description: t('menu.description.tickets'),
      exactMatch: false,
      icon: IconMessage,
      label: t('menu.title.tickets'),
      path: '/berichtenbox',
      to: `/berichtenbox${ticketFilterParams ?? ''}`,
    },
    {
      allowedRoles: [AuthorizationRoles.Tickets],
      description: t('menu.description.ticket_feedback'),
      exactMatch: false,
      icon: IconHotelClass,
      label: t('menu.title.ticket_feedback'),
      to: '/ticketfeedback',
    },
    {
      allowedRoles: [AuthorizationRoles.KnowledgeArticles],
      description: t('menu.description.knowledge_base'),
      exactMatch: true,
      icon: IconSchool,
      label: t('menu.title.knowledge_base'),
      to: `/kennisbank${knowledgeFilterParams ?? ''}`,
    },
    {
      description: t('menu.description.news'),
      exactMatch: false,
      icon: IconLocalLibrary,
      label: t('menu.title.news'),
      to: '/nieuws',
    },
    {
      description: t('menu.description.ideas'),
      exactMatch: false,
      icon: IconLightbulb,
      label: t('menu.title.ideas'),
      to: '/ideeen',
    },
    {
      allowedRoles: [AuthorizationRoles.CprRead],
      description: t('menu.description.contact_person_register'),
      exactMatch: false,
      icon: IconContactPage,
      label: t('menu.title.contact_person_register'),
      to: '/contactpersonenregister',
    },
    {
      allowedMarketRoles: ['LV', 'GDS', 'RNB', 'LNB'],
      allowedRoles: [AuthorizationRoles.CprRead, AuthorizationRoles.SuperUser],
      description: t('menu.description.invoice_contact_register'),
      exactMatch: false,
      icon: IconRequestPage,
      label: t('menu.title.invoice_contact_register'),
      to: '/factuurcontactregister',
    },
    {
      allowedRoles: [AuthorizationRoles.Tickets],
      description: t('menu.description.connection_information'),
      exactMatch: false,
      icon: IconCable,
      label: t('menu.title.connection_information'),
      to: '/aansluitingsinformatie',
    },
    {
      allowedRoles: [AuthorizationRoles.DataRequest],
      description: t('menu.description.data_services'),
      exactMatch: false,
      icon: IconCellWifi,
      label: t('menu.title.data_services'),
      to: '/datadiensten',
    },
    {
      allowedRoles: [AuthorizationRoles.MarketPartyRegistryReadAll],
      allowedMarketRoles: ['LV', 'GDS', 'RNB', 'LNB'],
      description: t('menu.description.market_party_registry'),
      exactMatch: false,
      icon: IconAccountBox,
      label: t('menu.title.market_party_registry'),
      to: '/marktpartijenregister',
    },
  ];

  return {
    bottom: [
      {
        allowedRoles: [
          AuthorizationRoles.SuperUser,
          AuthorizationRoles.CprAdmin,
          AuthorizationRoles.MarketPartyRegistryRead,
        ],
        description: t('menu.description.management'),
        exactMatch: false,
        icon: IconSettings,
        label: t('menu.title.management'),
        to: '/beheer',
      },
    ],
    main: mainMenuItems,
  };
};
